import Review, { ReviewOptions, ReviewType } from 'models/Review';
import Admission, { AdmissionOptions } from 'models/reviews/Admission';
import AltcsApplication, { AltcsApplicationOptions } from 'models/reviews/AltcsApplication';
import Authorization, { AuthorizationOptions } from 'models/reviews/Authorization';
import CoordinateRequest, { CoordinateRequestOptions } from 'models/reviews/CoordinateRequest';
import ProjectedDischarge, { ProjectedDischargeOptions } from 'models/reviews/ProjectedDischarge';
import Referral, { ReferralOptions } from 'models/reviews/Referral';
import SafeReturn, { SafeReturnOptions } from 'models/reviews/SafeReturn';
import ServiceRefusal, { ServiceRefusalOptions } from 'models/reviews/ServiceRefusal';

export default class ReviewModelFactory {
  private static defaultClass = Review;

  // The mapping is computed lazily because some review subclasses
  // (e.g. CoordinateRequest) sit inside an import cycle with this factory
  // via Activity → Patient → Episode → LocationEpisode → ReviewModelFactory.
  // A static field would evaluate during module initialization while those
  // bindings are still undefined; a method defers the lookup until call time.
  private static getMapping() {
    return {
      [ReviewType.ADMISSION]: Admission,
      [ReviewType.ALTCS_APPLICATION]: AltcsApplication,
      [ReviewType.AUTHORIZATION]: Authorization,
      [ReviewType.COORDINATE_REQUEST]: CoordinateRequest,
      [ReviewType.PROJECTED_DISCHARGE]: ProjectedDischarge,
      [ReviewType.REFERRAL]: Referral,
      [ReviewType.SERVICE_REFUSAL]: ServiceRefusal,
      [ReviewType.SAFE_RETURN]: SafeReturn,
    };
  }

  /**
   * Returns an instance of the appropriate class based on the options provided.
   * @see {@link https://www.typescriptlang.org/docs/handbook/2/functions.html#function-overloads Function Overloads}
   */
  static get(options: Partial<AuthorizationOptions>): Authorization;
  static get(options: Partial<AltcsApplicationOptions>): AltcsApplication;
  static get(options: Partial<CoordinateRequestOptions>): CoordinateRequest;
  static get(options: Partial<ProjectedDischargeOptions>): ProjectedDischarge;
  static get(options: Partial<ReferralOptions>): Referral;
  static get(options: Partial<SafeReturnOptions>): SafeReturn;
  static get(options: Partial<ServiceRefusalOptions>): ServiceRefusal;
  static get(options: Partial<AdmissionOptions>): Admission;
  static get(options: Partial<ReviewOptions>): Review;
  static get(options): Review {
    const mappedClass = options.type ? this.getMapping()[options.type] : this.defaultClass;
    const klass = mappedClass ?? this.defaultClass;
    return new klass(options);
  }
}
